import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import logo from "../assets/logo-mark.png";
import "./header.css";

const linksBefore = [
  { to: "/tours", label: "Tours" },
  { to: "/destinations", label: "Destinations" },
];

const safariCircuits = [
  {
    id: "northern",
    to: "/safaris/northern-circuits",
    label: "Northern Circuit",
    note: "Serengeti, crater, and classic parks",
    image:
      "https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=900&q=80",
    imageAlt: "Wildlife on open savanna plains",
    caption: "Northern Circuit",
    captionEm: "The classic Tanzania safari",
    parks: [
      {
        label: "Serengeti National Park",
        note: "Plains, predators, and migration",
        to: "/safaris/northern-circuits/serengeti",
      },
      {
        label: "Ngorongoro Crater",
        note: "Wildlife in a volcanic caldera",
        to: "/safaris/northern-circuits/ngorongoro",
      },
      {
        label: "Tarangire National Park",
        note: "Elephants and baobab country",
        to: "/safaris/northern-circuits/tarangire",
      },
      {
        label: "Lake Manyara",
        note: "Forest, lake edge, and birdlife",
        to: "/safaris/northern-circuits/lake-manyara",
      },
    ],
  },
  {
    id: "southern",
    to: "/safaris/southern-circuits",
    label: "Southern Circuit",
    note: "Ruaha, Nyerere, and quieter trails",
    image:
      "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=900&q=80",
    imageAlt: "Open highland and bush landscape",
    caption: "Southern Circuit",
    captionEm: "Wilder, less crowded parks",
    parks: [
      {
        label: "Ruaha National Park",
        note: "Big landscapes and strong game",
        to: "/safaris/southern-circuits/ruaha",
      },
      {
        label: "Nyerere National Park",
        note: "Rivers, boats, and remote bush",
        to: "/safaris/southern-circuits/nyerere",
      },
      {
        label: "Mikumi National Park",
        note: "Easy access from Dar or the south",
        to: "/safaris/southern-circuits/mikumi",
      },
    ],
  },
  {
    id: "combo",
    to: "/safaris/beach-and-safari",
    label: "Beach and Safari",
    note: "Mainland game days with a Zanzibar finish",
    image:
      "https://images.unsplash.com/photo-1506929562872-bb421503ef21?auto=format&fit=crop&w=900&q=80",
    imageAlt: "Tropical coastline for a beach finish",
    caption: "Beach and Safari",
    captionEm: "Bush first, island later",
    parks: [
      {
        label: "Northern plus Zanzibar",
        note: "Classic parks then the coast",
        to: "/safaris/beach-and-safari#northern-zanzibar",
      },
      {
        label: "Southern plus Zanzibar",
        note: "Ruaha or Nyerere then the island",
        to: "/safaris/beach-and-safari#southern-zanzibar",
      },
      {
        label: "Short safari add on",
        note: "Two to four game days before the beach",
        to: "/safaris/beach-and-safari#short-add-on",
      },
    ],
  },
];

const mountains = [
  {
    id: "kilimanjaro",
    to: "/trekking/kilimanjaro",
    label: "Mount Kilimanjaro",
    note: "Routes, pacing, and climb planning",
    image:
      "https://images.unsplash.com/photo-1609198092458-43ad304fbe58?auto=format&fit=crop&w=900&q=80",
    imageAlt: "Trekkers on Kilimanjaro",
    caption: "Mount Kilimanjaro",
    captionEm: "Africa’s highest peak",
    routes: [
      {
        label: "Machame Route",
        note: "Scenic southern approach",
        to: "/trekking/kilimanjaro#machame",
      },
      {
        label: "Lemosho Route",
        note: "Longer acclimatization",
        to: "/trekking/kilimanjaro#lemosho",
      },
      {
        label: "Marangu Route",
        note: "Hut based climb",
        to: "/trekking/kilimanjaro#marangu",
      },
      {
        label: "Rongai Route",
        note: "Quieter northern approach",
        to: "/trekking/kilimanjaro#rongai",
      },
    ],
  },
  {
    id: "meru",
    to: "/trekking/meru",
    label: "Mount Meru",
    note: "A sharp climb near Arusha",
    image:
      "https://images.unsplash.com/photo-1547471080-7cc2caa01a7e?auto=format&fit=crop&w=900&q=80",
    imageAlt: "Open highland landscape near Arusha",
    caption: "Mount Meru",
    captionEm: "Arusha National Park",
    routes: [
      {
        label: "Momella Route",
        note: "Standard Meru ascent",
        to: "/trekking/meru#momella",
      },
      {
        label: "3 Day Meru Trek",
        note: "Shorter summit schedule",
        to: "/trekking/meru#meru-3-day",
      },
      {
        label: "4 Day Meru Trek",
        note: "Extra rest before summit",
        to: "/trekking/meru#meru-4-day",
      },
    ],
  },
  {
    id: "ol-dionyo-lengai",
    to: "/trekking/ol-dionyo-lengai",
    label: "Ol Doinyo Lengai",
    note: "Night ascent of the Mountain of God",
    image:
      "https://images.unsplash.com/photo-1516426122078-c23e76319801?auto=format&fit=crop&w=900&q=80",
    imageAlt: "Dramatic landscape under open sky",
    caption: "Ol Doinyo Lengai",
    captionEm: "Mountain of God",
    routes: [
      {
        label: "Night Ascent",
        note: "Summit at first light",
        to: "/trekking/ol-dionyo-lengai#night-ascent",
      },
      {
        label: "Crater Rim Trek",
        note: "Ash slopes to the rim",
        to: "/trekking/ol-dionyo-lengai#crater-rim",
      },
    ],
  },
];

const linksAfter = [
  { to: "/trips", label: "Trips" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [safariOpen, setSafariOpen] = useState(false);
  const [trekOpen, setTrekOpen] = useState(false);
  const [activeCircuitId, setActiveCircuitId] = useState(safariCircuits[0].id);
  const [activeMountainId, setActiveMountainId] = useState(mountains[0].id);
  const safariRef = useRef(null);
  const trekRef = useRef(null);
  const location = useLocation();
  const safariActive = location.pathname.startsWith("/safaris");
  const trekActive = location.pathname.startsWith("/trekking");
  const activeCircuit =
    safariCircuits.find((circuit) => circuit.id === activeCircuitId) ||
    safariCircuits[0];
  const activeMountain =
    mountains.find((mountain) => mountain.id === activeMountainId) ||
    mountains[0];

  useEffect(() => {
    setMenuOpen(false);
    setSafariOpen(false);
    setTrekOpen(false);
  }, [location.pathname, location.hash]);

  useEffect(() => {
    if (!safariOpen) {
      setActiveCircuitId(safariCircuits[0].id);
    }
  }, [safariOpen]);

  useEffect(() => {
    if (!trekOpen) {
      setActiveMountainId(mountains[0].id);
    }
  }, [trekOpen]);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  useEffect(() => {
    function handleClick(event) {
      if (safariRef.current && !safariRef.current.contains(event.target)) {
        setSafariOpen(false);
      }
      if (trekRef.current && !trekRef.current.contains(event.target)) {
        setTrekOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  return (
    <header className="site-header sticky-top">
      <nav className="navbar navbar-expand-lg" aria-label="Primary">
        <div className="container site-header__bar">
          <Link
            to="/"
            className="navbar-brand site-header__brand d-flex align-items-center gap-3 mb-0"
            aria-label="Mirrorkhan Tours home"
          >
            <span className="site-header__logo-wrap">
              <img
                src={logo}
                alt=""
                className="site-header__logo"
                width="44"
                height="52"
                decoding="async"
              />
            </span>
            <span className="site-header__name">
              <span className="site-header__title">
                Mirrorkhan <em>Tours</em>
              </span>
              <span className="site-header__tag">Safari & Trips</span>
            </span>
          </Link>

          <button
            type="button"
            className={
              menuOpen
                ? "navbar-toggler site-header__toggle is-open"
                : "navbar-toggler site-header__toggle"
            }
            aria-expanded={menuOpen}
            aria-controls="primaryNav"
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="site-header__toggle-line" />
            <span className="site-header__toggle-line" />
          </button>

          <div
            id="primaryNav"
            className={
              menuOpen
                ? "collapse navbar-collapse show"
                : "collapse navbar-collapse"
            }
          >
            <ul className="navbar-nav ms-lg-auto align-items-lg-center gap-lg-3">
              {linksBefore.map((link) => (
                <li className="nav-item" key={link.to}>
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      isActive
                        ? "nav-link site-header__link is-active"
                        : "nav-link site-header__link"
                    }
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}

              <li
                className={
                  safariOpen
                    ? "nav-item site-header__dropdown is-open"
                    : "nav-item site-header__dropdown"
                }
                ref={safariRef}
                onMouseEnter={() => {
                  setTrekOpen(false);
                  setSafariOpen(true);
                }}
                onMouseLeave={() => setSafariOpen(false)}
              >
                <button
                  type="button"
                  className={
                    safariActive || safariOpen
                      ? "nav-link site-header__link site-header__drop-toggle is-active"
                      : "nav-link site-header__link site-header__drop-toggle"
                  }
                  aria-expanded={safariOpen}
                  aria-haspopup="true"
                  onClick={() => {
                    setTrekOpen(false);
                    setSafariOpen((open) => !open);
                  }}
                >
                  Safaris
                  <span className="site-header__caret" aria-hidden="true" />
                </button>

                <div
                  className={
                    safariOpen
                      ? "site-header__mega show"
                      : "site-header__mega"
                  }
                >
                  <div className="site-header__mega-panel">
                    <div className="row g-4 g-xl-5">
                      <div className="col-lg-4">
                        <p className="site-header__mega-label">Circuits</p>
                        <ul className="site-header__mega-list list-unstyled mb-0">
                          {safariCircuits.map((circuit) => (
                            <li
                              key={circuit.id}
                              onMouseEnter={() =>
                                setActiveCircuitId(circuit.id)
                              }
                            >
                              <NavLink
                                to={circuit.to}
                                className={
                                  activeCircuitId === circuit.id
                                    ? "site-header__mega-item is-active"
                                    : "site-header__mega-item"
                                }
                              >
                                <span>
                                  <strong>{circuit.label}</strong>
                                  <small>{circuit.note}</small>
                                </span>
                                <span
                                  className="site-header__mega-arrow"
                                  aria-hidden="true"
                                />
                              </NavLink>
                            </li>
                          ))}
                          <li>
                            <NavLink
                              to="/contact"
                              className="site-header__mega-item"
                            >
                              <span>
                                <strong>Plan a private safari</strong>
                                <small>Share dates, parks, and group size</small>
                              </span>
                              <span
                                className="site-header__mega-arrow"
                                aria-hidden="true"
                              />
                            </NavLink>
                          </li>
                        </ul>
                      </div>

                      <div className="col-lg-4">
                        <p className="site-header__mega-label">
                          {activeCircuit.label} parks
                        </p>
                        <ul className="site-header__mega-list list-unstyled mb-0">
                          {activeCircuit.parks.map((park) => (
                            <li key={park.label}>
                              <NavLink
                                to={park.to}
                                className="site-header__mega-item"
                              >
                                <span>
                                  <strong>{park.label}</strong>
                                  <small>{park.note}</small>
                                </span>
                                <span
                                  className="site-header__mega-arrow"
                                  aria-hidden="true"
                                />
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="col-lg-4">
                        <Link
                          to={activeCircuit.to}
                          className="site-header__mega-visual"
                        >
                          <img
                            src={activeCircuit.image}
                            alt={activeCircuit.imageAlt}
                          />
                          <span className="site-header__mega-caption">
                            {activeCircuit.caption}
                            <em>{activeCircuit.captionEm}</em>
                          </span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </li>

              <li
                className={
                  trekOpen
                    ? "nav-item site-header__dropdown is-open"
                    : "nav-item site-header__dropdown"
                }
                ref={trekRef}
                onMouseEnter={() => {
                  setSafariOpen(false);
                  setTrekOpen(true);
                }}
                onMouseLeave={() => setTrekOpen(false)}
              >
                <button
                  type="button"
                  className={
                    trekActive || trekOpen
                      ? "nav-link site-header__link site-header__drop-toggle is-active"
                      : "nav-link site-header__link site-header__drop-toggle"
                  }
                  aria-expanded={trekOpen}
                  aria-haspopup="true"
                  onClick={() => {
                    setSafariOpen(false);
                    setTrekOpen((open) => !open);
                  }}
                >
                  Trekking
                  <span className="site-header__caret" aria-hidden="true" />
                </button>

                <div
                  className={
                    trekOpen
                      ? "site-header__mega show"
                      : "site-header__mega"
                  }
                >
                  <div className="site-header__mega-panel">
                    <div className="row g-4 g-xl-5">
                      <div className="col-lg-4">
                        <p className="site-header__mega-label">Mountains</p>
                        <ul className="site-header__mega-list list-unstyled mb-0">
                          {mountains.map((mountain) => (
                            <li
                              key={mountain.id}
                              onMouseEnter={() =>
                                setActiveMountainId(mountain.id)
                              }
                            >
                              <NavLink
                                to={mountain.to}
                                className={
                                  activeMountainId === mountain.id
                                    ? "site-header__mega-item is-active"
                                    : "site-header__mega-item"
                                }
                              >
                                <span>
                                  <strong>{mountain.label}</strong>
                                  <small>{mountain.note}</small>
                                </span>
                                <span
                                  className="site-header__mega-arrow"
                                  aria-hidden="true"
                                />
                              </NavLink>
                            </li>
                          ))}
                          <li>
                            <NavLink
                              to="/contact"
                              className="site-header__mega-item"
                            >
                              <span>
                                <strong>Plan a private trek</strong>
                                <small>Tell us your dates and group</small>
                              </span>
                              <span
                                className="site-header__mega-arrow"
                                aria-hidden="true"
                              />
                            </NavLink>
                          </li>
                        </ul>
                      </div>

                      <div className="col-lg-4">
                        <p className="site-header__mega-label">
                          {activeMountain.label} routes
                        </p>
                        <ul className="site-header__mega-list list-unstyled mb-0">
                          {activeMountain.routes.map((route) => (
                            <li key={route.label}>
                              <NavLink
                                to={route.to}
                                className="site-header__mega-item"
                              >
                                <span>
                                  <strong>{route.label}</strong>
                                  <small>{route.note}</small>
                                </span>
                                <span
                                  className="site-header__mega-arrow"
                                  aria-hidden="true"
                                />
                              </NavLink>
                            </li>
                          ))}
                        </ul>
                      </div>

                      <div className="col-lg-4">
                        <Link
                          to={activeMountain.to}
                          className="site-header__mega-visual"
                        >
                          <img
                            src={activeMountain.image}
                            alt={activeMountain.imageAlt}
                          />
                          <span className="site-header__mega-caption">
                            {activeMountain.caption}
                            <em>{activeMountain.captionEm}</em>
                          </span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              </li>

              {linksAfter.map((link) => (
                <li className="nav-item" key={link.to}>
                  <NavLink
                    to={link.to}
                    className={({ isActive }) =>
                      isActive
                        ? "nav-link site-header__link is-active"
                        : "nav-link site-header__link"
                    }
                  >
                    {link.label}
                  </NavLink>
                </li>
              ))}

              <li className="nav-item mt-3 mt-lg-0">
                <Link
                  to="/contact"
                  className="btn btn-mk-gold site-header__cta"
                >
                  Plan a trip
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>
    </header>
  );
}

export default SiteHeader;
